/**
 * The dashboard — "before you load the trailer" (§8).
 *
 * Everything on this screen comes from `GET /garages/:id/dashboard` in one
 * request: the next event, what needs attention, the car tallies, the upcoming
 * spine. The server ran the projection engine to produce it (§4, §8), so this
 * file renders and does not compute — the one exception being the before/during
 * split, which is browser-local state with no API behind it (see lib/schedule).
 */

import { ArrowRight, Bell, Car, ChevronRight, Flag, MapPin, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import type { CarDto, DashboardResponse } from '@svcdue/contracts';

import { useDashboard } from '../api/hooks';
import { Empty, QueryState } from '../components/states';
import { Eyebrow, Tally, Tree } from '../components/primitives';
import { useGarageContext, usePermissions } from '../garage/GarageProvider';
import { useSchedule } from '../lib/schedule';
import { daysUntil, fmtShort, fmtWithWeekday } from '../lib/format';

export function Dashboard({ openAddCar, openAddEvent }: { openAddCar: () => void; openAddEvent: () => void }) {
  const { garageId } = useGarageContext();
  const query = useDashboard(garageId);

  return (
    <QueryState query={query}>
      {(data) => <DashboardView data={data} openAddCar={openAddCar} openAddEvent={openAddEvent} />}
    </QueryState>
  );
}

/**
 * Split from the query so it stays a pure function of its data — which is what
 * lets a test render it against fixtures with no network in the way.
 */
export function DashboardView({
  data,
  openAddCar,
  openAddEvent,
}: {
  data: DashboardResponse;
  openAddCar: () => void;
  /** Optional so a test can render the view without wiring the modal. */
  openAddEvent?: () => void;
}) {
  const navigate = useNavigate();
  const { canManageFleet, canLogMaintenance } = usePermissions();
  const { planFor } = useSchedule();

  const next = data.nextEvent;

  /**
   * Anything the user chose to run *during* the event drops out of the pre-tow
   * list and into the between-rounds block. The server lists everything in
   * `attention` because it has no idea about that choice — the split is ours.
   */
  const during = data.attention.filter((a) => planFor(a.itemId) === 'during');
  const before = data.attention.filter((a) => planFor(a.itemId) !== 'during');

  return (
    <div className="screen">
      <Eyebrow>Staging lanes</Eyebrow>
      <h1 className="h1">{data.garageName}</h1>

      <div className="hero">
        <div className="card hero-tree">
          <div className="card-h">
            <Flag size={13} /> Tree status
          </div>
          <div className="tree-wrap">
            <Tree animate />
            <div className="tree-key">
              <div className="tk">
                <i className="dot d-amber" />
                <div>
                  <b>Due soon</b>
                  <span>before your next event</span>
                </div>
              </div>
              <div className="tk">
                <i className="dot d-green" />
                <div>
                  <b>Good</b>
                  <span>within interval</span>
                </div>
              </div>
              <div className="tk">
                <i className="dot d-red" />
                <div>
                  <b>Overdue</b>
                  <span>past its trigger</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card timeslip">
          <div className="ts-h">
            <span>Next event</span>
            {/* Owner or crew: booking the weekend is a crew action (§2). */}
            {next && canLogMaintenance && openAddEvent && (
              <button className="ts-add" onClick={openAddEvent}>
                <Plus size={11} /> Add
              </button>
            )}
          </div>
          {next ? (
            <>
              <div className="ts-days">
                <b>{daysUntil(next.date)}</b> days out
              </div>
              <div className="ts-rows">
                <div>
                  <span>Event</span>
                  <b>{next.name}</b>
                </div>
                {/* Track is optional — a multi-track event has none. */}
                {next.track && (
                  <div>
                    <span>Track</span>
                    <b>{next.track}</b>
                  </div>
                )}
                <div>
                  <span>Date</span>
                  <b>{fmtWithWeekday(next.date)}</b>
                </div>
                <div>
                  <span>Running</span>
                  <b>
                    {data.cars.find((c) => c.car.id === next.carId)?.car.nickname ?? 'Car'} · est. {next.estPasses}{' '}
                    passes
                  </b>
                </div>
              </div>
              <div className="ts-perf" />
            </>
          ) : (
            <Empty
              icon={<Flag size={18} />}
              title="No event booked"
              hint="Add a planned event and the forecast will place service around it."
              action={
                canLogMaintenance && openAddEvent ? (
                  <button className="act" onClick={openAddEvent}>
                    <Plus size={12} /> Add event
                  </button>
                ) : undefined
              }
            />
          )}
        </div>

        <div className="card attention">
          <div className="card-h">
            <Bell size={13} /> Before you load the trailer
          </div>

          {data.attention.length === 0 ? (
            <Empty title="Everything's green" hint="Nothing is due before your next event." />
          ) : (
            <div className="att-list">
              {before.map((a) => (
                <div className="att-row" key={a.itemId}>
                  <span className={'lamp lamp-' + a.status} />
                  <div className="att-main">
                    <b>{a.itemName}</b>
                    <span>{a.who}</span>
                  </div>
                  <span className="att-note">{a.note}</span>
                </div>
              ))}
            </div>
          )}

          {during.length > 0 && (
            <div className="br-block">
              <div className="br-h">
                <MapPin size={11} /> At the track · between rounds
              </div>
              {during.map((a) => (
                <div className="att-row" key={a.itemId}>
                  <span className="lamp lamp-track" />
                  <div className="att-main">
                    <b>{a.itemName}</b>
                    <span>{a.who}</span>
                  </div>
                  <span className="att-note">{a.note}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="row-h">
        <h2 className="h2">Garage</h2>
        {/* Crew never see this: cars are owner-only to create (routes/fleet.ts). */}
        {canManageFleet && (
          <button className="link" onClick={openAddCar}>
            <Plus size={13} /> Add car
          </button>
        )}
      </div>

      {data.cars.length === 0 ? (
        <Empty
          icon={<Car size={20} />}
          title="No cars yet"
          hint={
            canManageFleet
              ? 'Add your first car to start tracking parts against it.'
              : 'The garage owner has not added a car yet.'
          }
          action={
            canManageFleet ? (
              <button className="act" onClick={openAddCar}>
                <Plus size={12} /> Add car
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="cars-grid">
          {data.cars.map((c) => (
            <button className="car-card" key={c.car.id} onClick={() => navigate(`/garage/${c.car.id}`)}>
              <div className="car-top">
                <div className="car-ic">
                  {c.car.imageUrl ? <img src={c.car.imageUrl} alt="" className="car-thumb" /> : <Car size={18} />}
                </div>
                <div className="car-id">
                  <b>{c.car.nickname}</b>
                  <span>{describeCar(c.car)}</span>
                </div>
                <ChevronRight size={16} className="car-chev" />
              </div>
              <Tally t={c.tally} />
              <div className="car-foot">
                <span>
                  <MapPin size={12} /> {c.car.useType === 'street' ? 'Street + Strip' : 'Strip only'}
                </span>
                {/* A strip-only car has no odometer worth showing — passes are
                    the only number that means anything to it (§2). */}
                <span>{metricFor(c.car, c.totalPasses)}</span>
              </div>
            </button>
          ))}
          {canManageFleet && (
            <button className="car-card car-add" onClick={openAddCar}>
              <div className="car-add-inner">
                <Plus size={20} />
                <b>Add car</b>
                <span>Year, make, drivetrain, class</span>
              </div>
            </button>
          )}
        </div>
      )}

      <div className="row-h">
        <h2 className="h2">Upcoming</h2>
        <button className="link" onClick={() => navigate('/timeline')}>
          Full timeline <ArrowRight size={13} />
        </button>
      </div>
      <MiniTimeline data={data} />
    </div>
  );
}

/** The next four things due — service crossings and expirations, interleaved. */
function MiniTimeline({ data }: { data: DashboardResponse }) {
  const rows = [
    ...data.upcoming.map((o) => ({ date: o.date, item: o.itemName, who: o.who, kind: 'proj' as const })),
    ...data.certs.map((c) => ({ date: c.date, item: c.itemName, who: c.who, kind: 'cert' as const })),
  ]
    .sort((a, b) => (a.date < b.date ? -1 : 1))
    .slice(0, 4);

  if (rows.length === 0) {
    return <Empty title="Nothing forecast yet" hint="Add parts with check conditions and they'll appear here." />;
  }

  return (
    <div className="mini-tl">
      {rows.map((t, i) => (
        <div className="mtl-row" key={`${t.item}-${t.date}-${i}`}>
          <span className="mtl-date">{fmtShort(t.date)}</span>
          <span className={'mtl-tick ' + (t.kind === 'cert' ? 'tick-cert' : 'tick-proj')} />
          <span className="mtl-item">{t.item}</span>
          <span className="mtl-who">{t.who}</span>
        </div>
      ))}
    </div>
  );
}

export function metricFor(car: Pick<CarDto, 'useType' | 'odometer'>, totalPasses: number): string {
  return car.useType === 'street' && car.odometer !== null
    ? `Odometer · ${car.odometer.toLocaleString('en-US')} mi`
    : `Total passes · ${totalPasses}`;
}

/** "1989 Ford Mustang GT · LS3 376 · Turbo" from whichever fields exist. */
export function describeCar(car: Pick<CarDto, 'year' | 'make' | 'model' | 'motorCode' | 'powerAdder'>): string {
  const identity = [car.year, car.make, car.model].filter(Boolean).join(' ');
  const spec = [car.motorCode, car.powerAdder].filter(Boolean).join(' · ');
  return [identity, spec].filter(Boolean).join(' · ') || 'No details yet';
}
